import { Injectable, Inject } from '@angular/core';
import { STATSIG_INIT_CONFIG, StatsigService } from '@statsig/angular-bindings';
import { StatsigOptions, StatsigUser } from '@statsig/js-client';
import { EMPTY, Observable, catchError, distinctUntilChanged, filter, from, map, of, switchMap, take } from 'rxjs';

import { ILogger } from '@shure/shared/angular/utils/logging';

import { FeatureFlagsProvider } from '../feature-flags-provider';

/**
 * Interface representing the user data required for managing feature flags.
 */
export type FeatureFlagUserData = StatsigUser;

/**
 * There is a TypeScript bug where it will not recognize the WithConfiguration union from the
 * StatsigInitConfig<StatsigClient> type. As a result, the `user` property cannot be found by the linter. Duplicating
 * the structure is sufficient for our needs.
 */
export type StatsigConfiguration = {
	sdkKey: string;
	user: StatsigUser;
	options?: StatsigOptions;
};

@Injectable({
	providedIn: 'root'
})
/**
 * Service for managing feature flags.
 */
export class StatsigProvider<FeatureFlagIds extends string, FeatureFlagsType> extends FeatureFlagsProvider<
	FeatureFlagIds,
	FeatureFlagsType
> {
	private _user: FeatureFlagUserData;
	public isLoading$ = this.statsigService.isLoading$;

	constructor(
		private readonly logger: ILogger,
		/** Statsig service for feature flag operations */
		private statsigService: StatsigService,
		@Inject(STATSIG_INIT_CONFIG) private statsigInitConfig: StatsigConfiguration
	) {
		super();
		this._user = this.statsigInitConfig.user;
	}

	/**
	 * Gets the state of a feature flag by its name.
	 *
	 * @param flagName - The name of the feature flag.
	 * @returns Returns an Observable. Its value is true if the flag is enabled. If the flag is set to false, an error
	 *          is encountered, or the flag does not exist, it will return false.
	 */
	public getFeatureFlag$(flagName: FeatureFlagIds): Observable<boolean> {
		return this.statsigService.isLoading$.pipe(
			filter((isLoading) => !isLoading),
			distinctUntilChanged(),
			map(() => {
				try {
					return this.statsigService.checkGate(<string>flagName);
				} catch (error) {
					//  If an error occurred, then log the error and assume the feature flag is false.
					this.logger.error('getFeatureFlag$', `Error checking feature flag ${flagName}`, error);
					return false;
				}
			})
		);
	}

	/**
	 * Gets the state of a feature flag by its name.
	 *
	 * @param flagName - The name of the feature flag.
	 * @returns Returns an Observable. Its value is same as the return type requested by
	 * consuming application
	 */
	public getDynamicConfig$(flagName: FeatureFlagIds): Observable<FeatureFlagsType> {
		return this.statsigService.isLoading$.pipe(
			filter((isLoading) => !isLoading),
			distinctUntilChanged(),
			map(() => {
				return <FeatureFlagsType>{
					data: this.statsigService.getDynamicConfig(<string>flagName).value
				};
			}),
			catchError((error) => {
				//  If an error occurred, then log the error and assume the dynamic flag is EMPTY.
				this.logger.error('getDynamicFlag$', `Error checking dynamic flag ${flagName}`, error);
				return EMPTY;
			})
		);
	}

	/**
	 * Sets the Statsig user object. Updating the user object may trigger a cache update.
	 * @param config The user object to be handed into Statsig.
	 */

	public setConfig$(config: object): Observable<void> {
		// Merge new config with initial custom properties
		const mergedConfig: FeatureFlagUserData = {
			...config,
			custom: {
				...this.statsigInitConfig.user.custom,
				...(<FeatureFlagUserData>config).custom
			}
		};

		return this.statsigService.isLoading$.pipe(
			filter((isLoading) => !isLoading),
			take(1),
			switchMap(() => {
				return from(this.updateUser(mergedConfig));
			}),
			catchError((error) => {
				this.logger.error('setConfig$', 'Error setting config', error);
				return of(undefined);
			})
		);
	}

	/**
	 * Retrieves the configuration object. For Statsig, this is known as the user object.
	 *
	 * @returns A clone of the user object. It is intended to be maintained by the updateUser function, which requires
	 * updating the user object in its entirety. This prevents the user object from being mutated through a reference.
	 */
	public getConfig(): FeatureFlagUserData {
		return { ...this._user };
	}

	/**
	 * Refreshes the cache.
	 *
	 * The Statsig SDK has no official refresh function, therefore updating the user object with itself accomplishes the
	 * same thing.
	 */
	public refreshCache(): Observable<void> {
		return from(this.updateUser(this._user));
	}

	/**
	 * Updates the user data.
	 *
	 * @param user - The user data to be updated.
	 * @returns An observable that completes when the update is complete.
	 */
	public updateUserObservable(user: FeatureFlagUserData): Observable<void> {
		return from(this.updateUser(user));
	}

	/**
	 * Indicates whether the service is currently loading.
	 *
	 * @returns An observable that emits a boolean indicating the loading state.
	 */
	public isLoading(): Observable<boolean> {
		return this.statsigService.isLoading$;
	}

	/**
	 * Updates the user data.
	 *
	 * @param user - The user data to be updated.
	 * @returns A promise that completes when the update is complete.
	 */
	private async updateUser(user: FeatureFlagUserData): Promise<void> {
		const result = await this.statsigService.updateUserAsync(user);
		if (result.error) {
			this.logger.error('updateUser', 'Error encountered while updating user for StatsigProvider', {
				user,
				result
			});
			throw result.error;
		}
		//  Store a copy of the user object if successful. Prevent changes to the object from outside of the service
		//  from affecting it.
		this._user = { ...user };
	}
}
